/**
 * VFXParticlesShared - Base class for WebGL/WebGPU particle implementations.
 */

import * as THREE from 'three';

import { AppearanceMode, RenderModeEnum } from '../types.js';
import { vfxStore } from '../VFXStore.js';

import type { VFXParticlesSettings } from '../types.js';

export interface EmitCallbackSettings {
  position: [number, number, number];
  direction: [number, number, number];
  scale: [number, number, number];
  rotation: [number, number, number];
  rotationSpeed: [number, number, number];
  lifetime: [number, number];
  colorStart: string;
  colorEnd: string;
  speed: [number];
}

export type EmitCallbackSettingsFn = () => EmitCallbackSettings;

export type VFXParticlesBackend = 'webgl' | 'webgpu';

export interface VFXParticlesConstructorOptions {
  backend?: VFXParticlesBackend;
  renderer?: unknown;
}

declare global {
  var __GENESYS_VFX_PARTICLES_BACKEND__: VFXParticlesBackend | undefined;
}

export interface VFXParticlesImplementation {
  dispose(): void;
  getMesh(): THREE.InstancedMesh;
  update(time?: number): void;
  updateSettings(settings: VFXParticlesSettings): void;
}

export type NormalizedVFXParticlesSettings =
  Omit<Required<VFXParticlesSettings>, 'appearance' | 'blendingMode' | 'shadingHooks'> & {
    appearance: AppearanceMode;
    blendingMode: THREE.Blending;
    shadingHooks: NonNullable<VFXParticlesSettings['shadingHooks']>;
  };

const tmpRotationEuler = new THREE.Euler();
const tmpColor = new THREE.Color();
const tmpIdentity = new THREE.Matrix4().identity();

function resolveBlendingMode(
  blendingMode: VFXParticlesSettings['blendingMode'] | THREE.Blending | undefined,
  fallback: THREE.Blending
): THREE.Blending {
  if (typeof blendingMode === 'number') {
    return blendingMode;
  }

  if (blendingMode === 'normal') return THREE.NormalBlending;
  if (blendingMode === 'additive') return THREE.AdditiveBlending;
  if (blendingMode === 'subtractive') return THREE.SubtractiveBlending;
  if (blendingMode === 'multiply') return THREE.MultiplyBlending;

  return fallback;
}

function resolveAppearance(
  appearance: VFXParticlesSettings['appearance'] | AppearanceMode | number | undefined,
  fallback: AppearanceMode
): AppearanceMode {
  if (typeof appearance === 'number') {
    return appearance === AppearanceMode.Circular ? AppearanceMode.Circular : AppearanceMode.Square;
  }

  if (appearance === 'circular') {
    return AppearanceMode.Circular;
  }

  if (appearance === 'square') {
    return AppearanceMode.Square;
  }

  return fallback;
}

function haveShadingHooksChanged(
  prev: NormalizedVFXParticlesSettings['shadingHooks'],
  next: NormalizedVFXParticlesSettings['shadingHooks']
): boolean {
  return prev.customUniforms !== next.customUniforms ||
    prev.customVaryings !== next.customVaryings ||
    prev.vertexBeforeMain !== next.vertexBeforeMain ||
    prev.vertexBeforeOutput !== next.vertexBeforeOutput ||
    prev.fragmentBeforeMain !== next.fragmentBeforeMain ||
    prev.fragmentBeforeOutput !== next.fragmentBeforeOutput;
}

export function resolveVFXParticlesBackend(
  options: VFXParticlesConstructorOptions = {}
): VFXParticlesBackend {
  if (options.backend) {
    return options.backend;
  }

  const renderer = options.renderer as {
    isWebGLRenderer?: boolean;
    isWebGPURenderer?: boolean;
  } | undefined;

  if (renderer?.isWebGPURenderer === true) {
    return 'webgpu';
  }

  if (renderer?.isWebGLRenderer === true) {
    return 'webgl';
  }

  if (globalThis.__GENESYS_VFX_PARTICLES_BACKEND__) {
    return globalThis.__GENESYS_VFX_PARTICLES_BACKEND__;
  }

  return 'webgpu';
}

export function normalizeVFXParticlesSettings(
  settings: VFXParticlesSettings = {},
  current?: NormalizedVFXParticlesSettings
): NormalizedVFXParticlesSettings {
  return {
    nbParticles: settings.nbParticles ?? current?.nbParticles ?? 1000,
    intensity: settings.intensity ?? current?.intensity ?? 1,
    renderMode: settings.renderMode ?? current?.renderMode ?? RenderModeEnum.Mesh,
    stretchScale: settings.stretchScale ?? current?.stretchScale ?? 1.0,
    fadeSize: settings.fadeSize ?? current?.fadeSize ?? [0.1, 0.9],
    fadeAlpha: settings.fadeAlpha ?? current?.fadeAlpha ?? [0, 1.0],
    gravity: settings.gravity ?? current?.gravity ?? [0, 0, 0],
    appearance: resolveAppearance(settings.appearance, current?.appearance ?? AppearanceMode.Square),
    easeFunction: settings.easeFunction ?? current?.easeFunction ?? 'easeLinear',
    blendingMode: resolveBlendingMode(settings.blendingMode, current?.blendingMode ?? THREE.AdditiveBlending),
    shadingHooks: settings.shadingHooks ?? current?.shadingHooks ?? {},
    side: settings.side ?? current?.side ?? THREE.FrontSide,
    depthTest: settings.depthTest ?? current?.depthTest ?? true,
    alphaMapPath: settings.alphaMapPath ?? current?.alphaMapPath ?? '',
    geometryPath: settings.geometryPath ?? current?.geometryPath ?? '',
  };
}

export abstract class VFXParticlesShared<TMaterial extends THREE.Material>
implements VFXParticlesImplementation {
  protected static readonly INSTANCE_STRIDE = 24;
  protected static readonly OFFSETS = {
    color: 0,
    colorEnd: 3,
    direction: 6,
    lifetime: 9,
    speed: 11,
    rotationSpeed: 12,
    position: 15,
    scale: 18,
    rotation: 21,
  } as const;

  protected readonly alphaMap?: THREE.Texture;
  protected readonly interleavedInstanceData: Float32Array;
  protected readonly mesh: THREE.InstancedMesh;
  protected readonly name: string;
  protected readonly startTimeSeconds: number;
  protected readonly store: ReturnType<typeof vfxStore.getState>;
  protected material: TMaterial;
  protected settings: NormalizedVFXParticlesSettings;
  protected cursor: number = 0;
  protected lastCursor: number = 0;
  protected needsUpdate: boolean = false;

  constructor(
    name: string,
    settings: VFXParticlesSettings = {},
    store?: ReturnType<typeof vfxStore.getState>,
    alphaMap?: THREE.Texture,
    geometry?: THREE.BufferGeometry
  ) {
    this.name = name;
    this.alphaMap = alphaMap;
    this.store = store ?? vfxStore.getState();
    this.startTimeSeconds = performance.now() * 0.001;
    this.settings = normalizeVFXParticlesSettings(settings);

    const defaultGeometry = geometry ?? new THREE.PlaneGeometry(0.5, 0.5);
    this.interleavedInstanceData = new Float32Array(
      this.settings.nbParticles * VFXParticlesShared.INSTANCE_STRIDE
    );
    this.material = this.createMaterial();
    this.applyCommonMaterialSettings(this.material);

    this.mesh = new THREE.InstancedMesh(
      defaultGeometry,
      this.material,
      this.settings.nbParticles
    );
    this.mesh.frustumCulled = false;

    this.setupAttributes();
    this.store.registerEmitter(name, this.emit.bind(this));
  }

  protected abstract createMaterial(): TMaterial;

  protected abstract updateMaterialUniforms(elapsedTime: number): void;

  protected applyCommonMaterialSettings(material: THREE.Material): void {
    material.transparent = true;
    material.blending = this.settings.blendingMode;
    material.depthWrite = false;
    material.side = this.settings.side;
    material.depthTest = this.settings.depthTest;
    material.needsUpdate = true;
  }

  protected recreateMaterial(): void {
    const previousMaterial = this.material;
    this.material = this.createMaterial();
    this.applyCommonMaterialSettings(this.material);
    this.mesh.material = this.material;
    previousMaterial.dispose();
  }

  private setupAttributes(): void {
    const geometry = this.mesh.geometry;
    const interleaved = new THREE.InstancedInterleavedBuffer(
      this.interleavedInstanceData,
      VFXParticlesShared.INSTANCE_STRIDE
    );
    interleaved.setUsage(THREE.DynamicDrawUsage);

    geometry.setAttribute(
      'instanceColor',
      new THREE.InterleavedBufferAttribute(interleaved, 3, VFXParticlesShared.OFFSETS.color)
    );
    geometry.setAttribute(
      'instanceColorEnd',
      new THREE.InterleavedBufferAttribute(interleaved, 3, VFXParticlesShared.OFFSETS.colorEnd)
    );
    geometry.setAttribute(
      'instanceDirection',
      new THREE.InterleavedBufferAttribute(interleaved, 3, VFXParticlesShared.OFFSETS.direction)
    );
    geometry.setAttribute(
      'instanceLifetime',
      new THREE.InterleavedBufferAttribute(interleaved, 2, VFXParticlesShared.OFFSETS.lifetime)
    );
    geometry.setAttribute(
      'instanceSpeed',
      new THREE.InterleavedBufferAttribute(interleaved, 1, VFXParticlesShared.OFFSETS.speed)
    );
    geometry.setAttribute(
      'instanceRotationSpeed',
      new THREE.InterleavedBufferAttribute(interleaved, 3, VFXParticlesShared.OFFSETS.rotationSpeed)
    );
    geometry.setAttribute(
      'instancePosition',
      new THREE.InterleavedBufferAttribute(interleaved, 3, VFXParticlesShared.OFFSETS.position)
    );
    geometry.setAttribute(
      'instanceScale',
      new THREE.InterleavedBufferAttribute(interleaved, 3, VFXParticlesShared.OFFSETS.scale)
    );
    geometry.setAttribute(
      'instanceRotation',
      new THREE.InterleavedBufferAttribute(interleaved, 3, VFXParticlesShared.OFFSETS.rotation)
    );
  }

  private emit(count: number, setup: EmitCallbackSettingsFn): void {
    const instanceColor = this.mesh.geometry.getAttribute('instanceColor') as THREE.InterleavedBufferAttribute;
    const instanceColorEnd = this.mesh.geometry.getAttribute('instanceColorEnd') as THREE.InterleavedBufferAttribute;
    const instanceDirection = this.mesh.geometry.getAttribute('instanceDirection') as THREE.InterleavedBufferAttribute;
    const instanceLifetime = this.mesh.geometry.getAttribute('instanceLifetime') as THREE.InterleavedBufferAttribute;
    const instanceSpeed = this.mesh.geometry.getAttribute('instanceSpeed') as THREE.InterleavedBufferAttribute;
    const instanceRotationSpeed = this.mesh.geometry.getAttribute('instanceRotationSpeed') as THREE.InterleavedBufferAttribute;
    const instancePosition = this.mesh.geometry.getAttribute('instancePosition') as THREE.InterleavedBufferAttribute;
    const instanceScale = this.mesh.geometry.getAttribute('instanceScale') as THREE.InterleavedBufferAttribute;
    const instanceRotation = this.mesh.geometry.getAttribute('instanceRotation') as THREE.InterleavedBufferAttribute;

    for (let i = 0; i < count; i++) {
      if (this.cursor >= this.settings.nbParticles) {
        this.cursor = 0;
      }

      const {
        scale,
        rotation,
        rotationSpeed,
        position,
        direction,
        lifetime,
        colorStart,
        colorEnd,
        speed,
      } = setup();

      tmpRotationEuler.set(...rotation);
      if (
        this.settings.renderMode === RenderModeEnum.Billboard ||
        this.settings.renderMode === RenderModeEnum.StretchBillboard
      ) {
        tmpRotationEuler.x = 0;
        tmpRotationEuler.y = 0;
      }

      this.mesh.setMatrixAt(this.cursor, tmpIdentity);

      instancePosition.setXYZ(this.cursor, position[0], position[1], position[2]);
      instanceScale.setXYZ(this.cursor, scale[0], scale[1], scale[2]);
      instanceRotation.setXYZ(this.cursor, tmpRotationEuler.x, tmpRotationEuler.y, tmpRotationEuler.z);

      tmpColor.set(colorStart);
      instanceColor.setXYZ(this.cursor, tmpColor.r, tmpColor.g, tmpColor.b);
      tmpColor.set(colorEnd);
      instanceColorEnd.setXYZ(this.cursor, tmpColor.r, tmpColor.g, tmpColor.b);
      instanceDirection.setXYZ(this.cursor, direction[0], direction[1], direction[2]);
      instanceLifetime.setXY(this.cursor, lifetime[0], lifetime[1]);
      instanceSpeed.setX(this.cursor, speed[0]);
      instanceRotationSpeed.setXYZ(this.cursor, rotationSpeed[0], rotationSpeed[1], rotationSpeed[2]);

      this.cursor++;
      this.cursor = this.cursor % this.settings.nbParticles;
    }

    this.mesh.instanceMatrix.needsUpdate = true;
    instanceColor.needsUpdate = true;
    instanceColorEnd.needsUpdate = true;
    instanceDirection.needsUpdate = true;
    instanceLifetime.needsUpdate = true;
    instanceSpeed.needsUpdate = true;
    instanceRotationSpeed.needsUpdate = true;
    instancePosition.needsUpdate = true;
    instanceScale.needsUpdate = true;
    instanceRotation.needsUpdate = true;
    this.needsUpdate = true;
  }

  public update(time?: number): void {
    const elapsedTime = time ?? ((performance.now() * 0.001) - this.startTimeSeconds);

    this.updateMaterialUniforms(elapsedTime);

    if (this.needsUpdate) {
      this.updateAttributeRanges();
      this.needsUpdate = false;
    }
  }

  private updateAttributeRanges(): void {
    const attributes = [
      this.mesh.instanceMatrix,
      this.mesh.geometry.getAttribute('instanceColor'),
      this.mesh.geometry.getAttribute('instanceColorEnd'),
      this.mesh.geometry.getAttribute('instanceDirection'),
      this.mesh.geometry.getAttribute('instanceLifetime'),
      this.mesh.geometry.getAttribute('instanceSpeed'),
      this.mesh.geometry.getAttribute('instanceRotationSpeed'),
      this.mesh.geometry.getAttribute('instancePosition'),
      this.mesh.geometry.getAttribute('instanceScale'),
      this.mesh.geometry.getAttribute('instanceRotation'),
    ];

    attributes.forEach((attr) => {
      const attribute = attr as THREE.BufferAttribute;
      attribute.needsUpdate = true;
    });

    this.lastCursor = this.cursor;
  }

  public getMesh(): THREE.InstancedMesh {
    return this.mesh;
  }

  /** Scene the mesh is under; avoids relying on `Object3D.scene` typings. */
  protected getMeshScene(): THREE.Scene | null {
    let current: THREE.Object3D | null = this.mesh;
    while (current) {
      if (current instanceof THREE.Scene) {
        return current;
      }
      current = current.parent;
    }
    return null;
  }

  public updateSettings(settings: VFXParticlesSettings): void {
    const nextSettings = normalizeVFXParticlesSettings(settings, this.settings);
    const shouldRebuildMaterial = nextSettings.renderMode !== this.settings.renderMode ||
      haveShadingHooksChanged(this.settings.shadingHooks, nextSettings.shadingHooks);

    this.settings = nextSettings;
    this.applyCommonMaterialSettings(this.material);

    if (shouldRebuildMaterial) {
      this.recreateMaterial();
    }
  }

  public dispose(): void {
    this.store.unregisterEmitter(this.name);
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
